import type {Dispatch} from 'redux'
import {db, FirebaseTimestamp} from '../../firebase'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment, limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
  WithFieldValue
} from "firebase/firestore";
import type {Image} from './types';
import {
  addProductAction,
  changeProductStatus,
  deleteProductAction,
  resetReceivedDateAction,
  saveReceivedDateAction,
  setProductsAction,
  updateProductAction
} from './actions';
import {RootState} from '../store/store';
import {ProductInCart} from '../users/types';
import toast from 'react-hot-toast';
import {orderConverter, orderInfoConverter, productConverter} from "../../firebase/converters";
import {resetCartAction} from '../users/actions';
import {getYYYYMMDD} from '../../utils/dateUtils';
import {Order} from "../orders/types";
import {NavigateFunction} from 'react-router-dom';

const productsRef = collection(db, "products");
const productsDeletedRef = collection(db, "products_deleted");

// 商品を削除する
export const deleteProduct = (id: string) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      // 削除前に商品データを取得
      const productRef = doc(productsRef, id);
      const productSnapshot = await getDoc(productRef);
      
      if (!productSnapshot.exists()) {
        toast.error('商品が見つかりません');
        return;
      }
      
      const productData = productSnapshot.data();
      
      // products_deletedコレクションに削除時刻と共に保存
      const deletedProductData = {
        ...productData,
        deleted_at: serverTimestamp()
      };
      
      await setDoc(doc(productsDeletedRef, id), deletedProductData);
      
      // productsコレクションから削除
      await deleteDoc(productRef);
      
      // storeの商品リストを更新
      const prevProducts = getState().products.list;
      const newProducts = prevProducts.filter((product: any) => product.id !== id);
      dispatch(deleteProductAction(newProducts));
      
      toast.success('商品を削除しました');
    } catch (error) {
      toast.error('商品の削除に失敗しました');
      console.error('Error deleting product:', error);
    }
  }
}

export const hiddenProduct = (id: string) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    // 商品の表示ステータスを変更
    const productRef = doc(productsRef, id);
    const productSnapshot = await getDoc(productRef);
    const productData = productSnapshot.data();
    const updatedProduct = { ...productData, status: "hidden" };

    await setDoc(productRef, updatedProduct).then(() => {
      toast.success('非表示に変更しました');
    });
  }
}

export const showProduct = (id: string) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    // 商品の表示ステータスを変更
    const productRef = doc(productsRef, id);
    const productSnapshot = await getDoc(productRef);
    const productData = productSnapshot.data();
    const updatedProduct = { ...productData, status: "show" };

    await setDoc(productRef, updatedProduct).then(() => {
      toast.success('表示に変更しました');
    });
  }
}

// firestoreから商品情報を取得する
export const fetchProducts = () => {
  return async (dispatch: Dispatch) => {
    const q = query(productsRef, orderBy('sort_order', 'asc')).withConverter(productConverter);
    const snapshot = await getDocs(q);
    const products = snapshot.docs.map(d => d.data());
    dispatch(setProductsAction(products));
    dispatch(changeProductStatus('succeeded'));
  }
}

// firestoreから商品情報を取得する
export const streamProducts = () => {
  return async (dispatch: Dispatch) => {
    const q = query(productsRef, orderBy('updated_at', 'desc')).withConverter(productConverter);
    onSnapshot(q, (snapshot) => {
      const changes = snapshot.docChanges();

      for (const change of changes) {
        const data = change.doc.data();

        if (change.type === "added") {
          dispatch(addProductAction(data))
        } else if (change.type === "modified") {
          dispatch(updateProductAction(data));
        }
      }
    });
    dispatch(changeProductStatus('succeeded'));
  }
}

export const saveReceivedDate = (received_date: Timestamp) => {
  return (dispatch: Dispatch) => {
    dispatch(resetCartAction());
    dispatch(saveReceivedDateAction(received_date));
  }
}

export const resetReceivedDate = () => {
  return (dispatch: Dispatch) => {
    dispatch(resetReceivedDateAction());
  }
}

// 商品を注文する
export const orderProduct = (productsInCart: Array<ProductInCart>, amount: number, note: string, received_Timestamp: Timestamp, navigate: NavigateFunction) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const uid = getState().users.uid;
    const yyyymmdd = getYYYYMMDD(received_Timestamp.toDate());
    const notZeroProductsInCart = productsInCart.filter(pc => pc.quantity > 0);

    if (notZeroProductsInCart.length === 0) {
      return;
    }

    try {
      await runTransaction(db, async (transaction) => {
        // 売切れの商品リスト 名前が入る
        const soldOutProductNames: Array<string> = [];

        // transaction は read を先に終わらせないとなので, productsInCart 内の商品を全て取得
        const fetchProductPromises = notZeroProductsInCart.map(productInCart =>
          transaction.get(doc(productsRef, productInCart.id).withConverter(productConverter)))
        const productSnapshots = await Promise.all(fetchProductPromises);
        const products = productSnapshots.map(snapshot => snapshot.data());

        // users/{userId/info/orderInfoを取得する
        const orderInfoRef = doc(db, `users/${uid}/info/orderInfo`).withConverter(orderInfoConverter);
        const orderInfoSnapshot = await transaction.get(orderInfoRef);
        const orderInfo = orderInfoSnapshot.data();
        const latestIndex = orderInfo ? (orderInfo.latestIndex + 1) : 1;

        // OrderInfo.indexを1増加
        if (orderInfo === undefined) {
          // orderInfoが存在しなかったら新規作成
          transaction.set(orderInfoRef, {
            latestIndex: latestIndex // Transaction だから increment 使ってない
          });
        } else {
          transaction.update(orderInfoRef, {
            latestIndex: latestIndex
          });
        }

        // 各商品について products/{productId}.ordered[yyyyMMdd] を注文数分加算
        for (const productInCart of notZeroProductsInCart) {
          const product = products.find(p => p && p.id === productInCart.id);
          // DBに商品が存在しなければ次のループへ
          if (product === undefined) continue;

          const ordered = product.ordered;
          const quantity = productInCart.quantity;

          //在庫から注文数を引いて、0以上であれば注文処理をする
          if (product.stocks[yyyymmdd] - (ordered[yyyymmdd] ?? 0) - quantity >= 0) {
            // カート内の在庫を更新
            transaction.update(doc(db, 'products', productInCart.id).withConverter(productConverter), {
              [`ordered.${yyyymmdd}`]: (yyyymmdd in ordered) ? increment(quantity) : quantity
            });
          } else {
            soldOutProductNames.push(productInCart.name);
          }
        }

        // 在庫切れのものがあれば注文処理を中断
        // TODO 購入数に対応させる
        // TODO 一部の商品が在庫切れの場合、在庫切れでない商品の在庫は減らされるが注文処理もされない
        if (soldOutProductNames.length > 0) {
          const errorMessage = (soldOutProductNames.length > 1) ? soldOutProductNames.join('と') : soldOutProductNames[0];
          alert('大変申し訳ありません。' + errorMessage + 'が在庫切れとなったため注文処理を中断しました');
          navigate('/')
          return Promise.reject(`Not enough stocks: ${errorMessage}`);
        }

        // users/{uid}/orders/{orderId} を追加
        const orderInUserRef = doc(collection(db, `users/${uid}/orders`)).withConverter(orderConverter);
        const orderId = orderInUserRef.id;

        const order: WithFieldValue<Order> = {
          amount: amount,
          created_at: serverTimestamp(),
          id: orderId,
          note: note,
          products: notZeroProductsInCart.filter(pc => pc.quantity > 0)
              .map(productInCart => ({
                id: productInCart.id,
                images: productInCart.images,
                name: productInCart.name,
                price: productInCart.price,
                quantity: productInCart.quantity,
              })),
          received_Timestamp: received_Timestamp,
          updated_at: serverTimestamp(),
          status: "added",
          index: latestIndex,
        };

        transaction.set(orderInUserRef, order);

        // archives/{yyyyMMdd}/orders/{orderId} を追加
        const orderInArchiveRef = doc(db, `archives/${yyyymmdd}/orders/${orderId}`);
        transaction.set(orderInArchiveRef, {
          ...order,
          order_id: orderId, // FIXME: order_id と id は中身同じ. order_id の使用箇所を id に変更した後に削除する
          firstNameKana: getState().users.firstNameKana,
          lastNameKana: getState().users.lastNameKana,
          phone: getState().users.phone,
        });
      })

      navigate('/order/complete')
      toast.success('注文が完了しました');

    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      return Promise.reject(e);
    }
  }
}

/**
 * カテゴリー内で最大の sort_order を返す
 * @param categoryId カテゴリーのID.
 */
const getLargestSortOrder = async (categoryId: string) => {
  const q = query(productsRef.withConverter(productConverter),
      limit(1),
      orderBy('sort_order', 'desc'),
      where('category', '==', categoryId)
  );
  const snapshot = await getDocs(q);
  // データがない場合は 0 を返す
  return snapshot.empty ? 0 : snapshot.docs[0].data().sort_order;
}

// 商品情報を保存する
export const saveProduct = (id: string, images: Array<Image>, name: string, description: string, category: string, price: string, navigate: NavigateFunction) => {
  return async (dispatch: Dispatch) => {
    if (name === '' || description === '' || price === '') {
      toast.error('必須項目が未入力です');
      return;
    }
    if (category === '') {
      toast.error('カテゴリーが未入力です');
      return;
    }
    // 現在の時刻をタイムスタンプ型で取得する
    const timestamp = FirebaseTimestamp.now().toDate();
    let yyyymmdd: Array<number> = [];
    for(let i = 0;i < 7;i++) {
      const date = new Date(timestamp);
      date.setDate(timestamp.getDate() + i + 1);
      const year = date.getFullYear();
      const month = ("00" + (date.getMonth() + 1)).slice(-2);
      const day = ("00" + date.getDate()).slice(-2);
      yyyymmdd[i] = Number(year + month + day);
    }

    let newData;
    if (id === '') {
      // 新しい商品の追加の場合
      const data = {
        category,
        description,
        images,
        name,
        price: parseInt(price, 10), // 10進数の数値に
        updated_at: timestamp,
        ordered: { [yyyymmdd[0]]: 0, [yyyymmdd[1]]: 0, [yyyymmdd[2]]: 0, [yyyymmdd[3]]: 0, [yyyymmdd[4]]: 0, [yyyymmdd[5]]: 0, [yyyymmdd[6]]: 0}, //TODO orders
        stocks: { [yyyymmdd[0]]: 0, [yyyymmdd[1]]: 0, [yyyymmdd[2]]: 0, [yyyymmdd[3]]: 0, [yyyymmdd[4]]: 0, [yyyymmdd[5]]: 0, [yyyymmdd[6]]: 0}, //TODO 反映できてるか検証
        status: "show",
      }
      // 新しくIDを自動生成
      const ref = doc(productsRef);

      // 本当はトランザクションで処理すべきだが，新規商品登録が複数件同時に行われるとは考えないため，通常のデータ取得で sort_order を得る
      // transactionにするのが面倒なだけ
      const largestSortOrder = await getLargestSortOrder(category);

      id = ref.id;
      newData = {
        ...data,
        created_at: timestamp,
        id: id,
        gas_name: name,
        is_recommended: false,
        sort_order: largestSortOrder + 1,
      }
    } else {
      // 既存の商品の更新の場合
      const data = {
        category,
        description,
        images,
        name,
        price: parseInt(price, 10), // 10進数の数値に
        updated_at: timestamp,
      }
      newData = {
        ...data,
      }
    }

    // 新しい商品情報をデータベースにセット
    return setDoc(doc(productsRef, id), {...newData}, { merge: true }).then(() => {
      navigate('/')
      toast.success('商品情報を保存しました');
    }).catch((error) => {
      throw new Error(error);
    });
  }
}

